import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, X } from "lucide-react";

const CATEGORIES = ["Breakfast", "Lunch", "Dinner", "Drinks", "Alcohol"];
const empty = { name: "", description: "", price: "", category: "Breakfast", subgroup: "", allergens: [], sort_order: 0 };

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Breakfast");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/menu").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const visible = items.filter((i) => i.category === filter);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, allergens: typeof form.allergens === "string" ? form.allergens.split(",").map((s) => s.trim()).filter(Boolean) : form.allergens };
    try {
      if (editing) await api.patch(`/menu/${editing}`, payload);
      else await api.post("/menu", payload);
      toast.success("Saved");
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch { toast.error("Failed to save"); }
  };

  const startEdit = (it) => {
    setEditing(it.id);
    setForm({ ...it, allergens: (it.allergens || []).join(", ") });
    setShowForm(true);
  };
  const remove = async (id) => { if (window.confirm("Delete this item?")) { await api.delete(`/menu/${id}`); load(); } };

  return (
    <div data-testid="admin-menu-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-4xl text-[#5979A9]">Menu Items</h1>
        <button onClick={() => { setEditing(null); setForm({ ...empty, category: filter }); setShowForm(true); }} data-testid="new-menu-item-button" className="btn-primary !py-2.5 !px-5 !text-sm">+ Add Item</button>
      </div>

      <div className="flex gap-2 overflow-x-auto mb-6 pb-2">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setFilter(c)}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm transition-colors ${filter === c ? "bg-[#E05947] text-white" : "bg-white border border-[#F0EAE6] text-[#2D3748]"}`}>
            {c} <span className="opacity-60">({items.filter((i) => i.category === c).length})</span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#F0EAE6] overflow-hidden">
        {visible.length === 0 ? <p className="p-6 text-sm text-[#4A5568]">No items in this category.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF4F1] text-[#5979A9]">
              <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3 hidden md:table-cell">Description</th><th className="text-left px-5 py-3">Price</th><th></th></tr>
            </thead>
            <tbody>
              {visible.map((it) => (
                <tr key={it.id} className="border-t border-[#F0EAE6]">
                  <td className="px-5 py-3 font-medium">{it.name}</td>
                  <td className="px-5 py-3 hidden md:table-cell text-[#4A5568] line-clamp-2 max-w-md">{it.description}</td>
                  <td className="px-5 py-3 text-[#E05947] font-semibold">{it.price}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => startEdit(it)} className="text-[#5979A9] mr-3"><Edit3 size={14}/></button>
                    <button onClick={() => remove(it.id)} className="text-[#E05947]"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-3xl max-w-lg w-full p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif-display text-2xl text-[#5979A9]">{editing ? "Edit Item" : "New Item"}</h2>
              <button onClick={() => setShowForm(false)}><X size={20}/></button>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <F label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <F label="Description" textarea value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
              <F label="Price" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required placeholder="€12.95" />
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <F label="Allergens (comma-separated codes)" value={form.allergens} onChange={(v) => setForm({ ...form, allergens: v })} placeholder="1, 3, 7" />
              <F label="Sort order" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) || 0 })} />
              <button type="submit" className="btn-primary w-full">Save Item</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, textarea, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2 resize-none" {...rest}/>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2" {...rest}/>
      )}
    </div>
  );
}
